/**
 * Where the session lives in the browser.
 *
 * ⚠️ `localStorage`, and the trade-off is stated rather than assumed. A token in
 * localStorage is readable by any script that runs on this origin, so it is only
 * acceptable because §12.11 requires a strict CSP and §12.1 keeps the access token
 * to fifteen minutes. The alternative — an httpOnly cookie — cannot be read by JS
 * at all, but the API is on a different subdomain (§14), so it would need CORS
 * credentials plus a CSRF defence, and the refresh token would still have to be
 * held somewhere for the mobile client which has no cookie jar.
 *
 * What follows from the choice: **never widen what is stored here.** No national
 * id, no phone number, no medical field (§12.3). The login response already avoids
 * them; this is the second place that must stay true.
 */
const STORAGE_KEY = 'aoun.session';

export type Session = {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: string;
};

export function readSession(): Session | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    // Narrowed rather than cast: a half-written or stale-shaped value would
    // otherwise reach the Authorization header as `undefined` and produce a 401
    // that looks like an expired session.
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'accessToken' in parsed &&
      'refreshToken' in parsed &&
      typeof (parsed as Session).accessToken === 'string' &&
      typeof (parsed as Session).refreshToken === 'string'
    ) {
      return parsed as Session;
    }

    clearSession();
    return null;
  } catch {
    clearSession();
    return null;
  }
}

export function writeSession(session: Session): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  window.dispatchEvent(new Event(SESSION_CHANGED));
}

export function clearSession(): void {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event(SESSION_CHANGED));
}

/**
 * Fired on every session change so React can react to a sign-out that happened
 * inside an axios interceptor — including one triggered by a failed refresh, which
 * is not a user action and therefore not tied to any component.
 */
export const SESSION_CHANGED = 'aoun:session-changed';
